import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import event_buffer from "../../context/event_def";
import { DocumentData } from '../../context/DocumentDataProvider';
import { TabNode } from 'flexlayout-react'; // Import TabNode to handle tab activation
import * as Menubar from '@radix-ui/react-menubar';
import { ChevronRightIcon, DotFilledIcon, PlayIcon, StopIcon, ResetIcon } from "@radix-ui/react-icons";



const separatorStyle: React.CSSProperties = {
    width: 2,
    height: 20,
    background: 'rgba(0,0,0,0.2)',
    margin: `0 ${5}px`,
    borderRadius: 1,
};

interface PlotlyTabProps {
    plotIndex: string;
    plots: Map<string, any>;
    setPlots: React.Dispatch<React.SetStateAction<Map<string, any>>>;
    node?: TabNode; // Add optional node prop to handle tab events
}

const HeatMapTab: React.FC<PlotlyTabProps> = ({ plotIndex, plots, setPlots, node }) => {
    const plotRef = useRef<HTMLDivElement>(null);
    const [plotId, setPlotId] = useState<string>(plotIndex);
    // axisScale holds the current axis scale types for the visible plot
    // keys: 'x' and 'y' values: 'linear' | 'log'
    const [axisScale, setAxisScale] = useState<{ x: 'linear' | 'log'; y: 'linear' | 'log' }>({ x: 'linear', y: 'linear' });
    // colorscale options and current selection
    const colorscaleOptions = ['Viridis', 'Cividis', 'Jet', 'Hot', 'Rainbow', 'YlGnBu', 'RdBu', 'Greys'];
    const [selectedColorscale, setSelectedColorscale] = useState<string>(colorscaleOptions[0]);

    // normalize layout so that empty titles do not reserve space and margins are minimal
    const normalizeLayout = (layout?: any) => {
        const l = Object.assign({}, layout || {});

        // detect if a meaningful title exists
        const hasTitle = !!(l.title && ((typeof l.title === 'string' && l.title.trim() !== '') || (l.title && typeof l.title.text === 'string' && l.title.text.trim() !== '')));
        if (!hasTitle) {
            // remove title entirely so Plotly doesn't reserve space for it
            delete l.title;
        }

    // prefer autosize and reasonable margins so plot takes maximum space
    // but leaves room for axis tick numbers (avoids clipping)
    l.autosize = true;
    // sensible defaults: left and bottom must allow tick labels to appear
    const defaultMargin = { l: 50, r: 10, t: hasTitle ? 24 : 6, b: 40, pad: 0 };
    l.margin = Object.assign({}, defaultMargin, l.margin || {});

    // Ensure axis tick labels are shown unless explicitly disabled in incoming layout
    l.xaxis = Object.assign({ showticklabels: true }, l.xaxis || {});
    l.yaxis = Object.assign({ showticklabels: true }, l.yaxis || {});

        return l;
    };

    // Helper to build a plotly-compatible figure from input
    const buildFigureFromPlotData = (plotData: any) => {
        if (!plotData) return null;

        // If the incoming object already looks like a figure/traces, return it
        if (plotData.data && (Array.isArray(plotData.data) || typeof plotData.data === 'object')) {
            return { data: plotData.data, layout: plotData.layout || {}, config: plotData.config || {} };
        }

        // Support heatmap-style input: { z: [...], x: [...], y: [...], layout?, config? }
        if (plotData.z) {
            const trace: any = {
                type: 'heatmap',
                z: plotData.z,
            };
            if (plotData.x) trace.x = plotData.x;
            if (plotData.y) trace.y = plotData.y;
            if (plotData.colorscale) trace.colorscale = plotData.colorscale;
            if (plotData.colorbar) trace.colorbar = plotData.colorbar;
            if (plotData.zmin !== undefined) trace.zmin = plotData.zmin;
            if (plotData.zmax !== undefined) trace.zmax = plotData.zmax;

            return { data: [trace], layout: plotData.layout || {}, config: plotData.config || {} };
        }

        // If plotData looks like raw traces array
        if (Array.isArray(plotData)) return { data: plotData, layout: {}, config: {} };

        // Unknown shape -> return null
        return null;
    };

    useEffect(() => {
        console.log("PlotlyTab mounted with plotId:", plotId);
        const plotData = plots.get(plotId);
        if (plotData) {
            const fig = buildFigureFromPlotData(plotData);
            if (fig) {
                const layout = normalizeLayout(fig.layout);
                try {
                    Plotly.react(plotRef.current, fig.data, layout, fig.config || {});
                } catch (e) {
                    console.warn('Plotly react error (heatmap):', e);
                }
            }
        }
    }, [plots]);

    // Update internal axisScale state from the plot layout (when plots change)
    useEffect(() => {
        const plotData = plots.get(plotId);
        if (!plotData) return;
        const layout = plotData.layout || {};
        const xType = layout.xaxis && layout.xaxis.type ? layout.xaxis.type : 'linear';
        const yType = layout.yaxis && layout.yaxis.type ? layout.yaxis.type : 'linear';
        setAxisScale({ x: xType === 'log' ? 'log' : 'linear', y: yType === 'log' ? 'log' : 'linear' });
    }, [plots, plotId]);

    // Sync initial colorscale selection from plot data when plots change
    useEffect(() => {
        const plotData = plots.get(plotId);
        if (!plotData) return;
        const fig = buildFigureFromPlotData(plotData);
        if (!fig || !Array.isArray(fig.data)) return;
        for (const t of fig.data) {
            if (t && t.type === 'heatmap' && t.colorscale) {
                const cs = typeof t.colorscale === 'string' ? t.colorscale : colorscaleOptions[0];
                setSelectedColorscale(cs);
                return;
            }
        }
    }, [plots, plotId]);

    // Helper: set axis scale and update plot
    const setAxisType = (axis: 'x' | 'y', type: 'linear' | 'log') => {
        const plotEl = plotRef.current;
        const plotData = plots.get(plotId);
        if (!plotData || !plotEl) return;

        // ensure layout exists
        const newLayout = Object.assign({}, plotData.layout || {});
        if (axis === 'x') {
            newLayout.xaxis = Object.assign({}, newLayout.xaxis || {}, { type: type === 'log' ? 'log' : 'linear' });
        } else {
            newLayout.yaxis = Object.assign({}, newLayout.yaxis || {}, { type: type === 'log' ? 'log' : 'linear' });
        }

        // update map copy and state
        const newPlots = new Map(plots);
        const updated = Object.assign({}, plotData, { layout: newLayout });
        newPlots.set(plotId, updated);
        setPlots(newPlots);

        // apply relayout to Plotly
        try {
            const relayoutObj: any = {};
            if (axis === 'x') relayoutObj['xaxis.type'] = type === 'log' ? 'log' : 'linear';
            else relayoutObj['yaxis.type'] = type === 'log' ? 'log' : 'linear';
            Plotly.relayout(plotEl, relayoutObj);
        } catch (e) {
            console.warn('Plotly relayout error:', e);
        }

        setAxisScale(prev => ({ ...prev, [axis]: type }));
    };

    // Helper: set colorscale for heatmap traces and update plot
    const setColorscale = (cs: string) => {
        const plotEl = plotRef.current;
        const plotData = plots.get(plotId);
        if (!plotData || !plotEl) return;

        // deep clone to avoid mutation
        let updated: any;
        try {
            updated = JSON.parse(JSON.stringify(plotData));
        } catch (e) {
            updated = Object.assign({}, plotData);
        }

        if (Array.isArray(updated.data)) {
            updated.data = updated.data.map((t: any) => (t && t.type === 'heatmap' ? Object.assign({}, t, { colorscale: cs }) : t));
        } else if (updated && updated.type === 'heatmap') {
            updated.colorscale = cs;
        }

        const newPlots = new Map(plots);
        newPlots.set(plotId, updated);
        setPlots(newPlots);

        try {
            Plotly.restyle(plotEl, { colorscale: cs });
        } catch (e) {
            console.warn('Plotly restyle error (colorscale):', e);
        }

        setSelectedColorscale(cs);
    };

    // Export helpers
    const download = (content: string, filename: string, mime = 'text/plain') => {
        const blob = new Blob([content], { type: mime + ';charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportCSV = () => {
        const plotData = plots.get(plotId);
        if (!plotData || !plotData.data) return;
        const fig = buildFigureFromPlotData(plotData);
        if (!fig) return;

        // If heatmap-style (single heatmap trace)
        if (Array.isArray(fig.data) && fig.data.length === 1 && fig.data[0].type === 'heatmap') {
            const t = fig.data[0];
            const z = t.z || [];
            const xs = Array.isArray(t.x) ? t.x : [];
            const ys = Array.isArray(t.y) ? t.y : [];

            // CSV: first column is an empty header, then x headers; subsequent rows: y value + z row
            const header = [''].concat(xs.map((v: any) => String(v))).join(',');
            const rows: string[] = [header];
            for (let i = 0; i < z.length; i++) {
                const rowVals = z[i] || [];
                const row = [ys[i] !== undefined ? String(ys[i]) : ''].concat(rowVals.map((v: any) => (v === undefined || v === null ? '' : String(v)))).join(',');
                rows.push(row);
            }

            download(rows.join('\n'), `${plotId || 'heatmap'}.csv`, 'text/csv');
            return;
        }

        // Fallback: treat as generic traces array and keep previous behaviour
        const traces = fig.data as any[];
        const cols: string[] = [];
        const arrays: any[][] = [];
        traces.forEach((t, i) => {
            const xs = Array.isArray(t.x) ? t.x : [];
            const ys = Array.isArray(t.y) ? t.y : [];
            cols.push(`trace${i + 1}_x`, `trace${i + 1}_y`);
            arrays.push(xs, ys);
        });

        const maxLen = arrays.reduce((m, arr) => Math.max(m, arr.length), 0);
        const rows2: string[] = [];
        rows2.push(cols.join(','));
        for (let r = 0; r < maxLen; r++) {
            const row: string[] = [];
            for (let c = 0; c < arrays.length; c++) {
                const v = arrays[c][r];
                row.push(v === undefined || v === null ? '' : String(v));
            }
            rows2.push(row.join(','));
        }

        download(rows2.join('\n'), `${plotId || 'plot'}.csv`, 'text/csv');
    };

    const exportCode = (format: 'python' | 'matlab') => {
        const plotData = plots.get(plotId);
        if (!plotData || !plotData.data) return;
        const fig = buildFigureFromPlotData(plotData);
        if (!fig) return;
        const traces = fig.data as any[];
        const layout = fig.layout || {};

        const getAxisTitle = (ax: any, fallback: string) => {
            if (!ax) return fallback;
            if (typeof ax.title === 'string') return ax.title;
            if (ax.title && typeof ax.title.text === 'string') return ax.title.text;
            return fallback;
        };

        const xTitle = getAxisTitle(layout.xaxis, 'x');
        const yTitle = getAxisTitle(layout.yaxis, 'y');
        const xType = (layout.xaxis && layout.xaxis.type) || axisScale.x || 'linear';
        const yType = (layout.yaxis && layout.yaxis.type) || axisScale.y || 'linear';

        // If heatmap-style, produce appropriate code
        if (traces.length === 1 && traces[0].type === 'heatmap') {
            const t = traces[0];
            const xs = t.x || [];
            const ys = t.y || [];
            const z = t.z || [];

            if (format === 'python') {
                // Use pcolormesh for non-uniform axes or imshow for uniform
                const toPy = (arr: any[]) => JSON.stringify(arr)
                    .replace(/\bnull\b/g, 'None')
                    .replace(/\btrue\b/g, 'True')
                    .replace(/\bfalse\b/g, 'False');

                let code = "import matplotlib.pyplot as plt\nimport numpy as np\n\n";
                code += `x = ${toPy(xs)}\n`;
                code += `y = ${toPy(ys)}\n`;
                code += `z = ${toPy(z)}\n\n`;
                code += "X, Y = np.meshgrid(x, y)\nfig, ax = plt.subplots()\n";
                code += "pcm = ax.pcolormesh(X, Y, z, shading='auto')\nfig.colorbar(pcm, ax=ax)\n";
                code += `ax.set_xlabel(${JSON.stringify(xTitle)})\n`;
                code += `ax.set_ylabel(${JSON.stringify(yTitle)})\n`;
                code += "plt.show()\n";

                download(code, `${plotId || 'heatmap'}.py`, 'text/x-python');
                return;
            } else {
                // Matlab code using imagesc or pcolor
                const xsMat = JSON.stringify(xs).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                const ysMat = JSON.stringify(ys).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                const zMat = JSON.stringify(z).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                let code = `x = ${xsMat};\ny = ${ysMat};\nz = ${zMat};\n\n`;
                code += "figure; pcolor(x, y, z); shading interp; colorbar;\n";
                code += `xlabel(${JSON.stringify(xTitle)});\n`;
                code += `ylabel(${JSON.stringify(yTitle)});\n`;
                download(code, `${plotId || 'heatmap'}.m`, 'text/x-matlab');
                return;
            }
        }

        // Fallback: existing trace-array logic (line plots etc.)
        if (format === 'python') {
            // Use matplotlib for Python export
            let code = "import matplotlib.pyplot as plt\n\n";

            // helper to convert JSON-style arrays to Python literals (None for null)
            const toPy = (arr: any[]) => {
                try {
                    return JSON.stringify(arr)
                        .replace(/\bnull\b/g, 'None')
                        .replace(/\btrue\b/g, 'True')
                        .replace(/\bfalse\b/g, 'False');
                } catch (e) {
                    return '[]';
                }
            };

            traces.forEach((t, i) => {
                code += `x${i + 1} = ${toPy(t.x || [])}\n`;
                code += `y${i + 1} = ${toPy(t.y || [])}\n`;
            });

            code += '\nfig, ax = plt.subplots()\n';
            traces.forEach((t, i) => {
                const name = t.name ? String(t.name).replace(/'/g, "\\'") : `trace${i + 1}`;
                code += `ax.plot(x${i + 1}, y${i + 1}, label='${name}')\n`;
            });

            if (xType === 'log') code += "ax.set_xscale('log')\n";
            if (yType === 'log') code += "ax.set_yscale('log')\n";

            code += `ax.set_xlabel(${JSON.stringify(xTitle)})\n`;
            code += `ax.set_ylabel(${JSON.stringify(yTitle)})\n`;
            code += 'ax.legend()\nplt.show()\n';

            download(code, `${plotId || 'plot'}.py`, 'text/x-python');
        } else {
            // Matlab export: include axis labels and axis scale
            let code = '';
            traces.forEach((t, i) => {
                const xs = JSON.stringify(t.x || []).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                const ys = JSON.stringify(t.y || []).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                code += `x${i + 1} = ${xs};\n`;
                code += `y${i + 1} = ${ys};\n`;
            });

            code += '\nfigure; hold on;\n';
            traces.forEach((t, i) => {
                const name = t.name ? String(t.name).replace(/'/g, "'\\''") : `trace${i + 1}`;
                // Matlab legend labels are set via legend() later; plot without name
                code += `plot(x${i + 1}, y${i + 1});\n`;
            });

            // set axis scales
            if (xType === 'log') code += `set(gca, 'XScale', 'log');\n`;
            if (yType === 'log') code += `set(gca, 'YScale', 'log');\n`;

            // labels
            code += `xlabel(${JSON.stringify(xTitle)});\n`;
            code += `ylabel(${JSON.stringify(yTitle)});\n`;

            // add legend with series names if available
            const names = traces.map((t, i) => (t.name ? String(t.name) : `trace${i + 1}`));
            if (names.length) {
                const matlabNames = names.map(n => `'%s'`.replace('%s', n.replace(/'/g, "'\\''"))).join(', ');
                code += `legend(${matlabNames});\n`;
            }

            code += 'hold off;\n';
            download(code, `${plotId || 'plot'}.m`, 'text/x-matlab');
        }
    };

    useEffect(() => {
        const el = plotRef.current;
        const pd = plots.get(plotId);
        if (!el || !pd) return;

        const data = pd.data ? pd.data : pd;
        const layout = normalizeLayout(pd.layout);
        try {
            Plotly.newPlot(el, data, layout, pd.config || {});
        } catch (e) {
            console.warn('Plotly newPlot error:', e);
        }
        if (!el || !(window as any).ResizeObserver || !(window as any).MutationObserver) return;

        const handleResize = () => {
            if (el.offsetParent !== null) {
                try {
                    Plotly.Plots.resize(el);
                } catch (e) {
                    console.warn('Plotly resize error:', e);
                }
            }
        };

        const containerEl = el.closest('.lm_item');
        const resizeObserver = new (window as any).ResizeObserver(handleResize);
        if (containerEl) resizeObserver.observe(containerEl);

        const mutationObserver = new (window as any).MutationObserver(() => {
            if (el.offsetParent !== null) {
                setTimeout(() => {
                    try {
                        Plotly.Plots.resize(el);
                    } catch (e) {}
                }, 0);
            }
        });
        mutationObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });

        // Listen for tab activation
        const handleTabActivation = () => {
            setTimeout(() => {
                try {
                    Plotly.Plots.resize(el);
                } catch (e) {
                    console.warn('Plotly resize error on tab activation:', e);
                }
            }, 0);
        };

        if (node) {
            node.setEventListener('resize', handleTabActivation);
        }

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (node) {
                node.setEventListener('resize', () => {}); // Provide an empty function to clear the listener
            }
        };
    }, [plots, node]);

    return (
        <>
            <Menubar.Root className="menubar" style={{ position: 'relative', zIndex: 10 , left: 5, top: 5}}>
                {/* Axis menu */}
                <Menubar.Menu>
                    <Menubar.Trigger className="menubar-trigger">Axis</Menubar.Trigger>
                    <Menubar.Content className="menubar-content" sideOffset={1} align="start" style={{ zIndex: 10 }}>
                        <div style={{ padding: '6px 8px' }}>
                            <div style={{ marginBottom: 6 }}><strong>X Axis</strong></div>
                            <Menubar.RadioGroup value={axisScale.x} onValueChange={(v) => setAxisType('x', v as 'linear' | 'log')}>
                                <Menubar.RadioItem className="menubar-item" value="linear">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Linear
                                </Menubar.RadioItem>
                                <Menubar.RadioItem className="menubar-item" value="log">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Logarithmic
                                </Menubar.RadioItem>
                            </Menubar.RadioGroup>
                            <div style={{ margin: '8px 0' }} />
                            <div style={{ marginBottom: 6 }}><strong>Y Axis</strong></div>
                            <Menubar.RadioGroup value={axisScale.y} onValueChange={(v) => setAxisType('y', v as 'linear' | 'log')}>
                                <Menubar.RadioItem className="menubar-item" value="linear">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Linear
                                </Menubar.RadioItem>
                                <Menubar.RadioItem className="menubar-item" value="log">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Logarithmic
                                </Menubar.RadioItem>
                            </Menubar.RadioGroup>
                        </div>
                    </Menubar.Content>
                </Menubar.Menu>

                {/* Colorscale menu */}
                <Menubar.Menu>
                    <Menubar.Trigger className="menubar-trigger">Colorscale</Menubar.Trigger>
                    <Menubar.Content className="menubar-content" sideOffset={1} align="start" style={{ zIndex: 10 }}>
                        <div style={{ padding: '6px 8px' }}>
                            <div style={{ marginBottom: 6 }}><strong>Colorscale</strong></div>
                            <Menubar.RadioGroup value={selectedColorscale} onValueChange={(v) => setColorscale(v)}>
                                {colorscaleOptions.map((cs) => (
                                    <Menubar.RadioItem className="menubar-item" key={cs} value={cs}>
                                        <Menubar.ItemIndicator className="menubarItemIndicator">
                                            <DotFilledIcon />
                                        </Menubar.ItemIndicator>
                                        {cs}
                                    </Menubar.RadioItem>
                                ))}
                            </Menubar.RadioGroup>
                        </div>
                    </Menubar.Content>
                </Menubar.Menu>

                {/* Export menu */}
                <Menubar.Menu>
                    <Menubar.Trigger className="menubar-trigger">Export</Menubar.Trigger>
                    <Menubar.Content className="menubar-content" sideOffset={1} align="start" style={{ zIndex: 10 }}>
                        <Menubar.Item className="menubar-item" onSelect={exportCSV}>CSV</Menubar.Item>
                        <Menubar.Item className="menubar-item" onSelect={() => exportCode('python')}>Python</Menubar.Item>
                        <Menubar.Item className="menubar-item" onSelect={() => exportCode('matlab')}>Matlab</Menubar.Item>
                    </Menubar.Content>
                </Menubar.Menu>
            </Menubar.Root>

            <div ref={plotRef} style={{ width: '100%', height: 'calc(100% - 36px)', position: 'relative', zIndex: 0 }} />
        </>
    );
};

export default HeatMapTab;